// Human-name helpers shared across surfaces. Data-quality feedback flagged that raw E2/Linear rows
// include internal test/demo rows (personal names used as placeholder districts, "BusRight Team"
// internal orders, etc.) that read as real customers if shown unfiltered. This module gives every
// surface ONE place to (a) detect a synthetic/internal-looking row and (b) pick the best human name
// to display — it does NOT rewrite any surface itself (that's the content pass); surfaces opt in by
// importing these helpers where they render recipient/district/account rows.

// Known synthetic/placeholder first-name-shaped test rows seen in the warehouse (e.g. a district
// named after an engineer used for local testing). Matched case-insensitively as a whole name token.
const SYNTHETIC_NAME_TOKENS: readonly string[] = [
  "szymon",
  "matt",
  "demo",
  "prod",
  "test",
] as const;

// Suffixes that mark an E2 recipient as BusRight-internal (never a customer), e.g.
// "BusRight - Jon Guyton" or "... BusRight Team".
const INTERNAL_NAME_MARKERS: readonly string[] = [
  "busright team",
  "busright -",
  "- busright",
] as const;

const normalize = (name: string): string => name.trim().toLowerCase();

// Splits a normalized name into words on whitespace/punctuation. Hoisted to module scope per
// Biome's useTopLevelRegex (a regex literal inside a hot function is recompiled on every call).
const WORD_SPLIT_RE = /[\s,()/-]+/;

// True when `name` looks like a synthetic/placeholder recipient (test district, demo account, an
// internal BusRight team row) rather than a real customer/district. Used to hide these rows from
// customer-facing worklists and rollups — never delete the data, just don't surface it as a account.
export const isSyntheticRecipient = (name: string | null | undefined): boolean => {
  if (!name) {
    return false;
  }
  const n = normalize(name);
  if (INTERNAL_NAME_MARKERS.some((marker) => n.includes(marker))) {
    return true;
  }
  // Match a synthetic token as a standalone word (e.g. "Szymon" or "Szymon Test District"), not as a
  // substring of an unrelated real name.
  const words = n.split(WORD_SPLIT_RE).filter(Boolean);
  return SYNTHETIC_NAME_TOKENS.some((token) => words.includes(token));
};

// The minimal shape a "row with a name" needs for `displayName` — matches the identity fields carried
// by RecipientIdentity / AccountActivityRow / DistrictRow / UnmatchedRecipient without importing all
// of them here (keeps this util dependency-free of any one contract shape).
export interface NamedRow {
  readonly displayName?: string | null;
  readonly rawName?: string | null;
  readonly accountName?: string | null;
  readonly label?: string | null;
  readonly id?: string | null;
}

// Human-name-first display convention: prefer a resolved display/account name, fall back to the raw
// E2 name, then a generic label, then the id, then "Unknown". Mirrors the ScopedEntityPage /
// ScopeSwitcher convention (names lead, ids are secondary) so every surface picks the same name.
export const displayName = (row: NamedRow): string =>
  row.displayName?.trim() ||
  row.accountName?.trim() ||
  row.rawName?.trim() ||
  row.label?.trim() ||
  row.id?.trim() ||
  "Unknown";
